import type { AccessKey } from '@/shared/permissions';
import type { LoginPlan } from '@/modules/auth/types';
import { isFreePlan } from '@/shared/access/plan-limits';
import { hasIntegration } from '@/shared/access/integration-entitlements';

/**
 * Plan-level entitlements layered on top of the Role Matrix. A permission can
 * be granted to a member yet still be locked by their workspace's plan (e.g.
 * the Free Builder plan). Both kinds of "no access" funnel into the same
 * Upgrade popup, whose CTA points at Billing.
 *
 * Keep this map small and intentional — only list features the plan actually
 * gates. Anything absent is considered plan-allowed (limited instead by the
 * Role Matrix grants and the usage/seat limits already enforced elsewhere).
 *
 * To gate a new module behind a plan, add an entry returning whether the plan
 * unlocks it.
 */
/**
 * Which column set of the public pricing table this plan reads.
 *
 * Realtor / solo accounts read the "agent" columns (Free · Starter · Growth ·
 * Pro); developer, builder AND team accounts read the "owner" columns (Free ·
 * Growth · Pro · Enterprise) — the same split `compareSetFor` makes in the
 * billing feature matrix, and the reason the tier NAMES differ between them.
 *
 * Deliberately `client_type`, not `category`: the catalog stores
 * `category = 'agent'` for team rows, which would put a team workspace in the
 * wrong column set.
 */
function isRealtorPlan(plan: LoginPlan): boolean {
    return plan.client_type === 'solo';
}

/**
 * Sending Identity — mail and text leads from the tenant's OWN domain and
 * number instead of the shared platform sender. Pricing rows "Email from
 * custom domain" + "SMS from your own number":
 *
 *   owner set   Free —   Growth ✓   Pro ✓   Enterprise ✓
 *   agent set   Free —   Starter —  Growth —  Pro ✓        (top tier only)
 */
function hasOwnSenderIdentity(plan: LoginPlan): boolean {
    return isRealtorPlan(plan) ? plan.tier_code === 'pro' : !isFreePlan(plan);
}

/**
 * Lead Routing — pricing row "Lead routing & assignment":
 *
 *   owner set   Free —   Growth ✓   Pro ✓   Enterprise ✓
 *   agent set   n/a on every tier — a solo realtor has no team to route TO,
 *               which is why the site prints "n/a" rather than "no".
 */
function hasLeadRouting(plan: LoginPlan): boolean {
    return !isRealtorPlan(plan) && !isFreePlan(plan);
}

/**
 * Role Matrix — configures what INVITED dashboard logins may do, so it is worth
 * showing only to a plan that can have some. The catalog answers that directly:
 * `limit_dashboard_users` is 0 on every Free tier and on every realtor tier,
 * and 2 / 5 / 15 on owner Growth / Pro / Enterprise.
 *
 * A null limit means "no cap" (the catalog's convention everywhere else), so it
 * passes; only an explicit 0 — nobody to grant a role to — hides the page.
 */
function hasTeamSeats(plan: LoginPlan): boolean {
    const seats = plan.limits.dashboard_users;
    return seats == null || seats > 0;
}

export const PLAN_FEATURE_GATES: Partial<Record<AccessKey, (plan: LoginPlan) => boolean>> = {
    // Analytics viewing is available on every plan (including Free) — intentionally
    // left ungated. Only the export action remains a plan entitlement.
    //
    // This tracks the pricing page's "Reporting & dashboards" row (Basic on Free,
    // Advanced on every paid tier) — NOT the Google Analytics integration flag,
    // which the plan-feature-parity migration narrowed to Enterprise + the
    // `analytics` add-on. Those are different products: GA drops the customer's
    // own tags onto buyer pages, while report export is part of Advanced reporting.
    'analytics.export': (p) => p.tier_code != null && p.tier_code !== 'free',
    // Data export (Leads / Properties / QR codes) is NOT plan-gated: any plan can
    // export. Access is governed only by the Role Matrix, so no entry here.
    // 'leads.export' intentionally omitted for this reason.

    // Sending Identity. `.view` is gated too, so a plan that doesn't sell it
    // never sees the card — the Settings hub renders only what `can()` allows,
    // and FeatureRoute sends the URL to /upgrade. (Earlier this deliberately
    // left `.view` open so Free could read the page and be upsold; the product
    // call is now to hide what the plan doesn't include.)
    'settings_sending_identity.view':   hasOwnSenderIdentity,
    'settings_sending_identity.create': hasOwnSenderIdentity,
    'settings_sending_identity.edit':   hasOwnSenderIdentity,
    'settings_sending_identity.delete': hasOwnSenderIdentity,

    // Lead Routing — hidden on Free, and hidden outright for realtor plans,
    // where the pricing page marks it n/a at every tier.
    'settings_lead_routing.view': hasLeadRouting,
    'settings_lead_routing.edit': hasLeadRouting,

    // Role Matrix — only where the plan includes dashboard logins to configure.
    // Every action, not just `.view`: with no seats there is nothing to create
    // or edit either.
    'role_matrix.view':   hasTeamSeats,
    'role_matrix.create': hasTeamSeats,
    'role_matrix.edit':   hasTeamSeats,
    'role_matrix.delete': hasTeamSeats,

    // Website Analytics is the dashboard for the "Website integration" the
    // pricing page sells — bundled on Pro/Enterprise, a paid add-on on Growth
    // and on every realtor tier, and not offered at all on Free.
    //
    // Without this entry `planAllows` fell through to `true`, so a Free tenant
    // typing /web-analytics walked straight in. Gating `.view` also drops the
    // sidebar item, which reads the same `can()` (see `sidebar-nav.ts`).
    //
    // `plan.features` is the EFFECTIVE entitlement (plan OR purchased add-on),
    // so a Growth tenant who buys the add-on passes here with no extra case.
    'web_analytics.view': (p) => hasIntegration(p, 'website_widget'),

    // Settings → Integrations stays READABLE on every plan — that page is where
    // the upsell lives, and hiding it would leave no way to discover what an
    // upgrade buys. Only writing is gated, and only when the plan unlocks
    // nothing at all; per-card locks are enforced on the page itself so a Pro
    // tenant can still edit the four they own while CRM stays locked.
    'settings_integrations.edit': (p) =>
        !p || INTEGRATION_FLAGS.some((flag) => hasIntegration(p, flag)),
};

/** Every integration flag — `settings_integrations.edit` needs at least one. */
const INTEGRATION_FLAGS = [
    'website_widget',
    'analytics_integration',
    'social_analytics',
    'email_marketing',
    'crm_integration',
] as const;

/**
 * Whether the plan unlocks a feature. Ungated features (and the case where no
 * plan info has loaded) are allowed — gating must never block on missing data.
 */
export function planAllows(feature: AccessKey, plan: LoginPlan | null): boolean {
    const gate = PLAN_FEATURE_GATES[feature];
    if (!gate) return true;
    if (!plan) return true;
    return gate(plan);
}
